import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, CheckCircle2, Info } from "lucide-react";
import { useEffect } from "react";

const ICONS = {
  error: AlertCircle,
  success: CheckCircle2,
  info: Info,
  warning: AlertCircle,
};

export default function GlobalNotificationPill({
  message = "",
  tone = "info",
  duration = 4200,
  onDismiss,
}) {
  const Icon = ICONS[tone] || Info;

  useEffect(() => {
    if (!message || !onDismiss) return undefined;

    const timeout = window.setTimeout(onDismiss, duration);
    return () => window.clearTimeout(timeout);
  }, [duration, message, onDismiss]);

  return (
    <AnimatePresence>
      {message ? (
        <motion.div
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -28, scale: 0.98 }}
          initial={{ opacity: 0, y: -34, scale: 0.98 }}
          role="status"
          style={styles.portal}
          transition={{ duration: 0.22, ease: "easeOut" }}
        >
          <div style={{ ...styles.wrap, ...(tone === "error" ? styles.wrapError : {}), ...(tone === "warning" ? styles.wrapWarning : {}) }}>
            <Icon size={18} strokeWidth={2.7} />
            <span style={styles.text}>{message}</span>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

const styles = {
  portal: {
    display: "flex",
    justifyContent: "center",
    left: 0,
    padding: "0 14px",
    pointerEvents: "none",
    position: "fixed",
    right: 0,
    top: "calc(14px + env(safe-area-inset-top))",
    zIndex: 9999,
  },
  wrap: {
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.96)",
    border: "1px solid rgba(148, 163, 184, 0.38)",
    borderRadius: 999,
    boxSizing: "border-box",
    boxShadow: "0 18px 54px rgba(15, 23, 42, 0.18)",
    color: "#0f172a",
    display: "flex",
    gap: 10,
    maxWidth: "min(640px, calc(100vw - 28px))",
    padding: "12px 18px",
    pointerEvents: "auto",
  },
  wrapError: {
    borderColor: "rgba(248, 113, 113, 0.45)",
    color: "#7f1d1d",
  },
  wrapWarning: {
    background: "rgba(255, 251, 235, 0.96)",
    borderColor: "rgba(245, 158, 11, 0.45)",
    color: "#92400e",
  },
  text: {
    fontSize: 14,
    fontWeight: 850,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
